/**
 * Blog queries — post listing, loading, creation, and updates.
 *
 * Centralizes all blog-related Supabase calls. Used by both
 * public blog pages and admin blog management.
 *
 * Used by: BlogIndex, BlogPost, BlogAdmin, BlogEditor, BlogPreview
 */
import { supabase } from '@/lib/supabase'
import { invokeEdgeJson } from './_edge'
import { SUMMARIZE_BLOG_POST } from './_endpoints'
import { insertOne, updateOneById } from './_helpers'

// ── Public ──────────────────────────────────────────────────────

/** Load all published blog posts (public listing). */
export async function fetchPublishedPosts() {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('id, title, slug, excerpt, cover_image_url, published_at')
    .eq('status', 'published')
    .order('published_at', { ascending: false })
  if (error) throw error
  return data || []
}

/** Load a single published blog post by slug. Returns the row or null. */
export async function fetchPublishedPost(slug) {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .eq('slug', slug)
    .eq('status', 'published')
    .maybeSingle()
  if (error) throw error
  return data
}

// ─��� Admin ───────────────────────────────────────────────────────

/** Load all blog posts for admin listing (all statuses). */
export async function fetchAllPosts() {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('id, title, slug, status, seo_score, published_at, created_at, excerpt')
    .order('created_at', { ascending: false })
  if (error) throw error
  return data || []
}

/** Load a single post by ID (admin editing). Returns the row or null. */
export async function fetchPostById(id) {
  const { data, error } = await supabase.from('blog_posts').select('*').eq('id', id).maybeSingle()
  if (error) throw error
  return data
}

/**
 * Load published posts that are missing at least one AI field.
 * Used by BlogAdmin's "Backfill AI copy" button to pick targets.
 * Returns just { id, title } — caller invokes summarizeBlogPost per row.
 */
export async function fetchPublishedPostsMissingAiCopy() {
  const { data, error } = await supabase
    .from('blog_posts')
    .select(
      'id, title, seo_description, ai_x_post, ai_ig_caption, ai_tiktok_hook, ai_youtube_community'
    )
    .eq('status', 'published')
  if (error) throw error
  return (data || []).filter(
    p =>
      !p.seo_description ||
      !p.ai_x_post ||
      !p.ai_ig_caption ||
      !p.ai_tiktok_hook ||
      !p.ai_youtube_community
  )
}

/** Load a single post by slug (admin preview — includes drafts). Returns the row or null. */
export async function fetchPostBySlug(slug) {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .eq('slug', slug)
    .maybeSingle()
  if (error) throw error
  return data
}

/** Create a new blog post. Returns the created post. */
export async function createPost(payload) {
  return insertOne('blog_posts', payload)
}

/** Update a blog post. Returns the updated post. */
export async function updatePost(id, payload) {
  return updateOneById('blog_posts', id, payload)
}

/** Toggle a blog post's published status. Only sets published_at on first publish. */
export async function togglePostStatus(id, newStatus, currentPublishedAt = null) {
  const update = { status: newStatus }
  if (newStatus === 'published' && !currentPublishedAt) {
    update.published_at = new Date().toISOString()
  }
  const { error } = await supabase.from('blog_posts').update(update).eq('id', id)
  if (error) throw error
}

/**
 * Invoke the summarize-blog-post edge function for a given post.
 * mode: 'auto' (fill nulls only, safe to fire on every publish) or
 *       'manual' (overwrite all 5 AI fields; BlogEditor gates this with a confirm).
 * Returns the updated post row; throws on error.
 */
export async function summarizeBlogPost(postId, mode) {
  const data = await invokeEdgeJson(
    SUMMARIZE_BLOG_POST,
    { post_id: postId, mode },
    { errorMessage: 'Failed to summarize blog post' }
  )
  return data?.post || null
}
